<template>
  <div class="document-view">
    <panel>
      <div class="card-body details-card">
        <div v-if="!withoutInfo && isSubmittedManually && !isDelivered" class="float-right">
          <p v-if="submittedManuallyText" v-html="submittedManuallyText" />

          <p v-else-if="submitToSchool">
            Wniosek został zapisany w systemie. Pobierz wniosek, wydrukuj, podpisz i dostarcz do szkoły, do której
            uczęszcza uczeń
          </p>

          <p v-else>
            Wniosek został zapisany w systemie. Pobierz wniosek, wydrukuj, podpisz i dostarcz do Wydziału Oświaty i
            Wychowania UM Lublin, ul. Narutowicza 37/39
          </p>
        </div>

        <check-permission
          v-if="!document.procedure.hidden"
          permissions="DOCUMENT_VIEW.PROCEDURE"
          :entity-type="entityType"
          :entity-id="document.id.toString()"
        >
          <p>
            Procedura:
            <strong>{{ document.procedure.name }}</strong>
          </p>
        </check-permission>

        <check-permission permissions="DOCUMENT_VIEW.ID" :entity-type="entityType" :entity-id="document.id.toString()">
          <p>
            Identyfikator:
            <strong>{{ document.id }}</strong>
          </p>
        </check-permission>

        <p v-if="document.systemNumbers && document.systemNumbers.mdok">
          Numer MDOK:
          <strong>{{ document.systemNumbers.mdok }}</strong>
        </p>

        <p v-if="document.systemNumbers && document.systemNumbers.sign">
          Znak sprawy:
          <strong>{{ document.systemNumbers.sign }}</strong>
        </p>

        <check-permission
          permissions="DOCUMENT_VIEW.STATUS"
          :entity-type="entityType"
          :entity-id="document.id.toString()"
        >
          <p>
            Status:
            <strong>{{ document.status.name }}</strong>
          </p>
        </check-permission>

        <check-permission
          v-if="isDelivered && documentIndex"
          permissions="DOCUMENT_VIEW.SUBMIT_DATE"
          :entity-type="entityType"
          :entity-id="document.id.toString()"
        >
          <p>
            Data złożenia:
            <strong>{{ documentIndex.values.DELIVERY_DATE | moment('DD-MM-YYYY') }}</strong>
          </p>
        </check-permission>

        <check-permission
          v-if="documentIndex && documentIndex.data && documentIndex.data.school"
          permissions="DOCUMENT_VIEW.SCHOOL_NAME"
          :entity-type="entityType"
          :entity-id="document.id.toString()"
        >
          <p>
            Szkoła:
            <strong> {{ documentIndex.data.school.schoolName }}</strong>
          </p>
        </check-permission>

        <check-permission
          v-if="studentFullName"
          permissions="DOCUMENT_VIEW.TENDERER"
          :entity-type="entityType"
          :entity-id="document.id.toString()"
        >
          <p>
            Uczeń:
            <strong>
              {{ studentFullName }}
            </strong>
          </p>
        </check-permission>

        <check-permission
          v-if="applicantOrTenderer"
          permissions="DOCUMENT_VIEW.APPLICANT"
          :entity-type="entityType"
          :entity-id="document.id.toString()"
        >
          <p>
            Wnioskodawca:
            <strong>
              {{ applicantOrTenderer }}
            </strong>
          </p>
        </check-permission>

        <check-permission
          permissions="DOCUMENT_VIEW.CHECKSUM"
          :entity-type="entityType"
          :entity-id="document.id.toString()"
        >
          <p>
            Suma kontrolna:
            <strong>{{ document.checksum }}</strong>
          </p>
        </check-permission>

        <check-permission
          v-if="!parentId && !withoutInfo"
          permissions="DOCUMENT_VIEW.SIGNATURE"
          :entity-type="entityType"
          :entity-id="document.id.toString()"
        >
          <p>
            Podpis elektroniczny:
            <strong>{{ signature(document.signedAttachment) }}</strong>
          </p>
        </check-permission>

        <p v-if="document.data && document.data.termPassedReason">
          Powód złożenia po terminie:
          <strong>{{ document.data.termPassedReason }}</strong>
        </p>

        <check-permission
          v-if="!parentId && !withoutInfo && isSubmitted && documentIndex && documentIndex.values"
          permissions="DOCUMENT_VIEW.RESPONSE_TYPE"
          :entity-type="entityType"
          :entity-id="document.id.toString()"
        >
          <p>
            Forma odpowiedzi:
            <strong v-if="documentIndex.values.EPUAP_ADDRESS">
              Elektronicznie, na skrzynkę: {{ documentIndex.values.EPUAP_ADDRESS }}
            </strong>

            <strong v-else>Papierowo</strong>
          </p>
        </check-permission>

        <check-permission
          v-if="!parentId && !withoutInfo && documentIndex && documentIndex.currentDecision"
          permissions="DOCUMENT_VIEW.DECISION"
          :entity-type="entityType"
          :entity-id="document.id.toString()"
        >
          <div>
            <p>
              Decyzja:
              <strong>{{ documentIndex.currentDecision.name }}</strong>
            </p>

            <div v-if="documentIndex.currentDecision.positive">
              <p v-if="documentIndex.currentDecision.data.amount">
                Kwota: <strong> {{ documentIndex.currentDecision.data.amount }} zł </strong>
              </p>

              <div v-if="scholarshipFormsRanges.length">
                <div><p>Forma świadczenia:</p></div>

                <ul class="dashed">
                  <li v-for="(dateRange, index) in scholarshipFormsRanges" :key="'dateRange_' + index">
                    <strong>
                      {{ dateRange.type | dateRangeTranslation }}: {{ dateRange.startDate | moment('DD-MM-YYYY') }} -
                      {{ dateRange.endDate | moment('DD-MM-YYYY') }}
                    </strong>
                  </li>
                </ul>
              </div>

              <div v-if="withdrawalPeriods.length">
                <p>Okresy cofnięcia pomocy:</p>

                <ul style="padding-left: 20px">
                  <li v-for="(dateRange, index) in withdrawalPeriods" :key="'dateRange_' + index">
                    <strong>
                      {{ dateRange.startDate | moment('DD-MM-YYYY') }} -
                      {{ dateRange.endDate | moment('DD-MM-YYYY') }}
                    </strong>
                  </li>
                </ul>
              </div>

              <div v-if="holdPeriods.length">
                <p>Okresy wstrzymania pomocy:</p>

                <ul style="padding-left: 20px">
                  <li v-for="(dateRange, index) in holdPeriods" :key="'dateRange_' + index">
                    <strong>
                      {{ dateRange.startDate | moment('DD-MM-YYYY') }} -
                      {{ dateRange.endDate | moment('DD-MM-YYYY') }}
                    </strong>
                  </li>
                </ul>
              </div>

              <p>
                Okres obowiązywania decyzji:
                <strong>
                  {{ documentIndex.currentDecision.startDate | moment('DD-MM-YYYY') }} -
                  {{ documentIndex.currentDecision.endDate | moment('DD-MM-YYYY') }}
                </strong>
              </p>
            </div>
          </div>
        </check-permission>

        <div v-if="!parentId" class="float-right">
          <check-permission
            v-if="!!mdokUid"
            permissions="DOCUMENT.MDOK"
            entity-type="document"
            :entity-id="document.id"
          >
            <w-button icon="fas fa-link mr-2" class="btn-light mr-2" text="Przejdź do MDOK" @click="goToMdok" />
          </check-permission>

          <w-button icon="fal fa-eye mr-2" class="btn-light mr-2" text="Podgląd" @click="openPreview" />

          <post-form
            v-if="isSubmitted"
            button-class="mr-2 btn-light"
            icon="fal fa-cloud-download mr-2"
            text="Pobierz"
            :url="renderUrl"
          />

          <system-message
            button-class="btn btn-light mr-2 "
            icon="fal fa-fw fa-bell"
            :entity-type="entityType"
            :entity-id="document.id.toString()"
            :name="messageName"
          />
        </div>
      </div>

      <actions
        slot="footer"
        :entity-type="entityType"
        :actions="actions"
        :object="document"
        @navigate="navigate"
        @execute="execute"
        @setStatus="openStatusPicker($event.object)"
        @preview="getPreview($event)"
      />
    </panel>

    <pick-status-modal
      :id="statusPickerDocumentId"
      :comment-enabled="true"
      :object="statusPickerDocument"
      :show="showStatusPicker"
      @update="saveStatus"
      @close="closeStatusPicker"
    />

    <schema-form-modal
      :object="actionData"
      :show="!!actionData"
      :external="{ documentIndex }"
      @save="sendModalData"
      @close="closeActionDataModal"
    />

    <preview-modal :show="showPreviewModal" :html="previewHTML" @close="closePreviewModal" />
  </div>
</template>

<script>
import { mapState } from 'vuex';

import Actions from '@/components/actions/Actions';
import SystemMessage from '@/components/formComponents/SystemMessage';
import PreviewModal from '@/components/modals/PreviewModal';
import PostForm from '@/components/postForm/PostForm';
import { DecisionCalculationTypeTranslation, DecisionCalculationType } from '@/enums/decisionCalculationType';
import { DocumentValueCodes } from '@/enums/documentValueCodes.enum';
import { Errors } from '@/enums/errors.enum';
import { PublicSettings } from '@/enums/publicSettings.enum';
import { StageType } from '@/enums/stageType.enum';
import { TableName } from '@/enums/tableName';
import { buttonsMixin } from '@/mixins/buttons';
import { documentManageMixin } from '@/mixins/document/documentManage';
import { documentRenderMixin } from '@/mixins/document/documentRender';
import { previewMixin } from '@/mixins/preview';
import { signatureMixin } from '@/mixins/signature';
import { documentResource } from '@/resources/document.resource';
import { SET_STATUS_ACTION } from '@/utils/constants';
import { getDocumentViewData } from '@/utils/document';

// @vuese
// Widok dokumentu
// @group Document
export default {
  components: { SystemMessage, Actions, PostForm, PreviewModal },
  filters: {
    dateRangeTranslation(code) {
      return DecisionCalculationTypeTranslation[code];
    }
  },
  mixins: [
    documentManageMixin,
    documentRenderMixin,
    buttonsMixin(StageType.DOCUMENT, [SET_STATUS_ACTION], getDocumentViewData), // musi być później, żeby nie nadpisała się metoda `afterStatusSave`
    previewMixin,
    signatureMixin
  ],
  data() {
    return {
      entityType: TableName.document
    };
  },
  computed: {
    ...mapState({
      document: state => state.document.data,
      documentIndex: state => state.documentIndex.data,
      withoutInfo: state => state.setting.settings[PublicSettings.PROCEDURE_WITHOUT_INFO]?.value === 'true',
      submittedManuallyText: state => state.setting.settings[PublicSettings.SUBMITTED_MANUALLY_TEXT]?.value || false,
      isSecretary: state => !!state.auth.nodes?.length
    }),
    mdokUid() {
      if (this.isSecretary) {
        return this.document.systemNumbers?.mdokJoId;
      }
      return this.document.systemNumbers?.mdokId;
    },
    parentId() {
      return this.$route.query.parentId;
    },
    messageName() {
      if (this.document.data && this.document.data.title) {
        return `Przypomnienie o wniosku: "${this.document.data.title}"`;
      }
      return `Przypomnienie o wniosku procedury: "${this.document.procedure.name}"`;
    },
    isSubmitted() {
      return !!this.documentIndex.values?.[DocumentValueCodes.SUBMIT_DATE];
    },
    isSubmittedManually() {
      return this.isSubmitted && !this.document.signedAttachment;
    },
    isDelivered() {
      return !!this.documentIndex.values?.[DocumentValueCodes.DELIVERY_DATE];
    },
    submitToSchool() {
      return this.documentIndex.data?.school?.financingType === 1;
    },
    applicantOrTenderer() {
      return this.documentIndex.data?.applicant?.fullName || this.documentIndex.data?.tenderer?.fullName;
    },
    studentFullName() {
      return (
        this.documentIndex.data?.employee?.fullName ||
        this.documentIndex.data?.tenderer?.fullName ||
        this.documentIndex.data?.student?.fullName
      );
    },
    scholarshipFormsRanges() {
      return this.documentIndex.currentDecision?.dateRanges.filter(
        dataRange =>
          dataRange.type === DecisionCalculationType.AMOUNT || dataRange.type === DecisionCalculationType.INVOICE
      );
    },
    withdrawalPeriods() {
      return this.documentIndex.currentDecision?.dateRanges.filter(
        dataRange => dataRange.type === DecisionCalculationType.WITHDRAWAL
      );
    },
    holdPeriods() {
      return this.documentIndex.currentDecision?.dateRanges.filter(
        dataRange => dataRange.type === DecisionCalculationType.HOLD
      );
    }
  },
  methods: {
    // @vuese
    // Przejście do MDOK
    goToMdok() {
      documentResource
        .getMdokUrlByUid(parseInt(this.mdokUid), this.document.id)
        .then(url => window.open(url, '_blank'))
        .catch(err => this.notifyError(Errors.MDOK_LINK_FAILED));
    }
  },

  beforeRouteUpdate(to, from, next) {
    getDocumentViewData(to)
      .then(() => next())
      .catch(() => this.notifyError(Errors.DOCUMENT_CANNOT_GET));
  }
};
</script>

<style>
.document-view ul.dashed {
  padding-left: 5px;
  list-style-type: none;
}
.document-view ul.dashed > li {
  text-indent: -5px;
}
.document-view ul.dashed > li:before {
  content: '-';
  text-indent: -5px;
}
</style>
